import IdeyaModal from '../../../../../common/antd/ideya-modal.tsx'
import { useGetBlockLeaveQuery } from '../../../../../apis/block-leave/block-leave-api.api.ts'
import { ColDef } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { marketDateTimezoneFormatter } from '../../../../../utils/formatters.ts'
import './user-edit-block-leave-modal.scss'
import { IdeyaButton } from '../../../../../common/antd/ideya-button.tsx'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { BlockLeaveData } from '../../../../../apis/block-leave/block-leave-api.def.ts'
import { Checkbox } from 'antd/lib/index'
import './user-block-leave-modal.scss'
import { CustomCellRendererProps } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'

interface UserEditBlockLeaveModalProps {
  userId: string
  showEditBlockLeaveModal: boolean
  handleCloseModal: () => void
  handleChangeLiquidate: (id: string, value: boolean) => void
  handleWithdraw: (id: string) => void
}

export const UserEditBlockLeaveModal: React.FC<
  UserEditBlockLeaveModalProps
> = ({
  userId,
  showEditBlockLeaveModal,
  handleCloseModal,
  handleChangeLiquidate,
  handleWithdraw,
}) => {
  const { t } = useTranslation()
  const { data, isLoading, isFetching } = useGetBlockLeaveQuery(
    { userId },
    {
      skip: !userId,
    }
  )

  const columns: ColDef[] = [
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.created_at'
      ),
      field: 'createdAt',
      valueFormatter: (params) =>
        marketDateTimezoneFormatter(
          {
            ...params,
            data: {
              ...params.data,
              marketTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          },
          { showTimezone: true }
        ),
      cellClass: (params) => (params.data?.deletedAt ? 'disabled-row' : ''),
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.leave_dates'
      ),
      field: 'leaveDates',
      valueGetter: (params) => {
        const { startDate, endDate } = params.data
        return `${startDate} ~ ${endDate}`
      },
      cellClass: (params) => (params.data?.deletedAt ? 'disabled-row' : ''),
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.liquidated_all_my_ideas'
      ),
      field: 'closeIdeas',
      cellClass: (params) => (params.data?.deletedAt ? 'disabled-row' : ''),
      cellRenderer: (params: ICellRendererParams<BlockLeaveData>) => {
        return (
          <Checkbox
            checked={params.data?.closeIdeas || false}
            onChange={(e) =>
              handleChangeLiquidate(params.data!.id, e.target.checked)
            }
          />
        )
      },
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.action'
      ),
      field: '',
      cellClass: (params) => (params.data?.deletedAt ? 'disabled-row' : ''),
      cellRenderer: (props: CustomCellRendererProps) => {
        const isDeleted = props.data?.deletedAt
        return (
          <IdeyaButton
            size={'small'}
            disabled={isDeleted}
            onClick={() => {
              handleWithdraw(props.data.id)
            }}
          >
            {isDeleted
              ? t(
                  'user_management.user_modal.block_leave_modal.columns.withdrawn'
                )
              : t(
                  'user_management.user_modal.block_leave_modal.columns.withdraw'
                )}
          </IdeyaButton>
        )
      },
    },
  ]

  return (
    <IdeyaModal
      open={showEditBlockLeaveModal}
      title={t('user_management.user_modal.block_leave_modal.edit_time_off')}
      destroyOnClose
      isForm={true}
      width={800}
      onCancel={handleCloseModal}
      className={'user-edit-block-leave-modal'}
      footer={
        <>
          <IdeyaButton onClick={handleCloseModal}>
            {t('common.got_it')}
          </IdeyaButton>
        </>
      }
    >
      <IdeyaAgGridTable
        columns={columns}
        loading={isLoading || isFetching}
        rowData={data?.data || []}
      />
    </IdeyaModal>
  )
}
